import { type HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'

export const ICON_SIZE_MAP = {
  marker: [20, 20],
  markerSelected: [24, 30],
  markerSelectedCompared: [24, 30],
  markerCluster: [40, 40],
  markerCampaignSelected: [20, 20],
}

async function getMarkerIcon(brand: string, name: string, http: HttpClient) {
  const iconUrl = `/assets/img/brands/${brand}/map/${name}`
  try {
    const icon = await firstValueFrom(
      http.get(`${iconUrl}.svg`, { responseType: 'text' }),
    )
    const isSVG = icon && icon.includes('<svg')
    return isSVG ? `${iconUrl}.svg` : `${iconUrl}.png`
  } catch (err) {
    console.warn(`SVG icon not found for ${name}, falling back to PNG`)
    return `${iconUrl}.png`
  }
}

export async function loadMapIcons(map: any, brand: string, http: HttpClient) {
  const keys = [
    'marker',
    'markerSelected',
    'markerSelectedCompared',
    'markerCluster',
    'markerCampaignSelected',
  ]

  const urls = await Promise.all([
    getMarkerIcon(brand, 'marker', http),
    getMarkerIcon(brand, 'marker-selected', http),
    getMarkerIcon(brand, 'marker-selected-compare', http),
    Promise.resolve(`/assets/img/brands/${brand}/map/marker-cluster.svg`),
    Promise.resolve(
      `/assets/img/brands/${brand}/map/marker-campaign-selected.svg`,
    ),
  ])

  function urlToImage({ url, width, height }): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
      const img = new Image(width, height)
      img.onload = () => resolve(img)
      img.src = url
    })
  }

  await Promise.all(
    urls.map(async (url, i) => {
      const key = keys[i]
      if (map.hasImage(key)) return
      const iconSize = ICON_SIZE_MAP[key]
      const image = await urlToImage({
        url,
        width: iconSize[0],
        height: iconSize[1],
      })
      map.addImage(key, image)
    }),
  )
}

interface PointsStyleConfig {
  allPoisInCampaign: boolean
  campaignPOIIds: string[]
  selectedPOI: string | null
  selectedComparePOI: string | null
}

export function getPointsStyle(config: PointsStyleConfig) {
  let iconImage: any = 'marker'

  if (config.allPoisInCampaign) {
    iconImage = 'markerCampaignSelected'
  } else {
    const matches = config.campaignPOIIds
      .map((id) => [id, ['image', 'markerCampaignSelected']])
      .reduce((acum, next) => acum.concat(next), [])

    if (config.selectedPOI) {
      matches.push(config.selectedPOI, ['image', 'markerSelected'])
    }
    if (config.selectedComparePOI) {
      matches.push(config.selectedComparePOI, [
        'image',
        'markerSelectedCompared',
      ])
    }

    iconImage = matches.length
      ? ['match', ['get', 'id'], ...matches, ['image', 'marker']]
      : 'marker'
  }

  return {
    'icon-image': iconImage,
  }
}

interface PointsFilterConfig {
  selectedPOI: string | null
  selectedComparePOI: string | null
  hasAllPOIsInPOIMap: boolean
}

export function getPointsFilter(config: PointsFilterConfig) {
  const applyFilter =
    config.selectedPOI &&
    config.selectedComparePOI &&
    !config.hasAllPOIsInPOIMap

  if (applyFilter) {
    const filters = ['in', 'id', config.selectedPOI]

    if (config.selectedComparePOI) {
      filters.push(config.selectedComparePOI)
    }

    return filters
  }

  return null
}
